import { z } from 'zod'
import { ISSUE } from '#model/support/errors'
import { visibleLine } from '#model/support/text'
import { baseUnitSchema, quantityCodec, quantitySchema } from '#model/values/units'

// 'dish' is here in 0.1, before any restaurant is: the 0.3 gate measures return
// separately for products and venues, and without the column there is nothing to split on.
export const itemKindSchema = z.enum(['product', 'dish'])
export type ItemKind = z.infer<typeof itemKindSchema>

// The four lengths a GTIN has — EAN-8, UPC-A, EAN-13, GTIN-14. A range of 8..14 looks
// equivalent and quietly accepts a mistyped nine digits.
export const barcodeSchema = z.string().regex(/^(\d{8}|\d{12,14})$/)

/** The longest name and note — for a form that stops at them rather than failing past them. */
export const ITEM_NAME_MAX = 200
export const ITEM_NOTE_MAX = 300

const nameSchema = visibleLine(ITEM_NAME_MAX)
const noteSchema = visibleLine(ITEM_NOTE_MAX)

/**
 * The most codes one item holds — one product in its packagings, not a list of anything. The
 * database has no trigger for it: the writer counts under the item's lock (MOL-100).
 */
export const ITEM_BARCODES_MAX = 20

const barcodesSchema = z
  .array(barcodeSchema)
  .max(ITEM_BARCODES_MAX)
  .refine((codes) => new Set(codes).size === codes.length, { error: ISSUE.BARCODE_DUPLICATED })
  .readonly()

export const itemSchema = z.object({
  id: z.uuid(),
  kind: itemKindSchema,
  name: nameSchema,
  /** Latin, filled by toSearchKey on write — which is why no input carries it. */
  // 800, not 200: transliteration grows a name, and by more than the alphabet suggests.
  // The tables at most double it («щ» becomes «sh», «я» becomes «ia»), but NFD decomposes
  // one Hangul syllable into three letters that nothing strips, so the real ceiling is
  // threefold — 200 × 3 = 600. Without the headroom a valid name would produce a key this
  // same schema refuses.
  searchKey: visibleLine(800),
  /** Several per item: one product comes in different packaging, and loose goods have none. */
  barcodes: barcodesSchema,
  note: noteSchema.nullable(),
  defaultUnit: baseUnitSchema,
  typicalQuantity: quantitySchema.nullable(),
  createdBy: z.uuid().nullable(),
  createdAt: z.date(),
})
export type Item = z.infer<typeof itemSchema>

/**
 * Where an item's data may have come from besides the person who proposed it (MOL-162). One value
 * today: `open_food_facts` — proposed with a code Open Food Facts named, so its name and size may be
 * that base's, which is ODbL: what is derived from it is shared on request under the same licence,
 * and the mark is what tells it from the rest of the catalogue. Set by the server, never sent.
 */
export const itemOriginSchema = z.enum(['open_food_facts'])
export type ItemOrigin = z.infer<typeof itemOriginSchema>

export const newItemSchema = z.strictObject({
  kind: itemKindSchema,
  name: nameSchema,
  barcodes: barcodesSchema.default([]),
  note: noteSchema.optional(),
  defaultUnit: baseUnitSchema,
  typicalQuantity: quantityCodec.optional(),
})
export type NewItem = z.infer<typeof newItemSchema>
